import EngineCleanupBase from '../../../services/engines/EngineCleanupBase';
import componentNotifier from '../../../services/wss/ComponentNotifier';
import csCodeTemplateCache from './CsCodeTemplateCache';
import csCodeTemplateFetcher from './CsCodeTemplateFetcher';
import { EngineNames } from '../../../services/wss/events/EngineNames';
import { MOCK_TEMPLATES, MOCK_DEFAULTS } from './CsCodeTemplateMocks';

const _name = EngineNames.CsCodeTemplateEngine;

/**
 * CsCodeTemplateEngine — provides raw %TOKEN%-based C# templates AND form default values
 * to the CsCodeGenWidget. Both live as notes in Jerny.CsCodeTemplates:
 * - code templates: type=CsCodeTemplate, title=strategyId
 * - form defaults: type=CsCodeGenDefaults, title='CsCodeGenDefaults', JSON body
 * Falls back to MOCK_TEMPLATES / MOCK_DEFAULTS when the cache is empty (BE not yet fetched /
 * offline dev) — this is required, not optional: CsCodeGenConfigEngine calls
 * csCodeGenFormState.createDefault() -> getDefaults() synchronously at module-load time (class field
 * initializer), before any fetchAll() has ever run. Without a mock fallback getDefaults() would
 * return null and crash the app on load.
 */
class CsCodeTemplateEngine extends EngineCleanupBase {
    /**
     * initialize engine
     */
    bootup = () => {
        this.initialize(_name);
    };

    /**
     * fetch all template + defaults notes from BE
     */
    fetchAll = async () => {
        await csCodeTemplateFetcher.fetch();
        this._notify();
    };

    /**
     * Gets the raw template body for a given strategyId (note title).
     * @param {string} strategyId - e.g. 'PluginClass', 'DataDbContext', 'PluginCsproj'
     * @returns {string|null} raw %TOKEN% template body, or null if not found anywhere
     */
    getTemplate = (strategyId) => {
        try {
            const items = csCodeTemplateCache.getAll();
            const found = items.find((x) => x.title === strategyId && x.type === 'CsCodeTemplate');
            if (found?.body) return found.body;

            // fallback to mock while BE cache is empty
            return MOCK_TEMPLATES[strategyId] ?? null;
        } catch (error) {
            this.logError('getTemplate', error.message);
            return MOCK_TEMPLATES[strategyId] ?? null;
        }
    };

    /**
     * Gets the full CsCodeTemplate note (rk, title, body, ...) for a given strategyId.
     * Unlike getTemplate(), returns the entry itself — needed by editors that must save
     * back to the note (entryUpdater.savePlainTextBody requires entry.rk).
     * No mock fallback: an entry that only exists as MOCK_TEMPLATES has no rk to save to,
     * so the editor caller must handle null (BE cache not fetched yet / unknown strategyId).
     * @param {string} strategyId - e.g. 'PluginClass', 'DataDbContext', 'PluginCsproj'
     * @returns {object|null} the raw note entry, or null if not present in the BE cache
     */
    getTemplateEntry = (strategyId) => {
        try {
            const items = csCodeTemplateCache.getAll();
            return items.find((x) => x.title === strategyId && x.type === 'CsCodeTemplate') ?? null;
        } catch (error) {
            this.logError('getTemplateEntry', error.message);
            return null;
        }
    };

    /**
     * Gets every known CsCodeTemplate title — used by CsCodeGenAutocompleteEdit (templateName field)
     * to suggest existing templates instead of the user having to remember/retype exact note titles.
     * Falls back to MOCK_TEMPLATES keys while the BE cache is empty, same fallback rule as getTemplate().
     * @returns {string[]}
     */
    getTemplateNames = () => {
        try {
            const items = csCodeTemplateCache.getAll();
            const fromCache = items.filter((x) => x.type === 'CsCodeTemplate').map((x) => x.title);
            return fromCache.length ? fromCache : Object.keys(MOCK_TEMPLATES);
        } catch (error) {
            this.logError('getTemplateNames', error.message);
            return Object.keys(MOCK_TEMPLATES);
        }
    };

    /**
     * Gets the CsCodeGenDefaults object (form default values), parsed from its JSON note body.
     * Falls back to MOCK_DEFAULTS while the BE cache is empty.
     * @returns {object}
     */
    getDefaults = () => {
        try {
            const items = csCodeTemplateCache.getAll();
            const found = items.find((x) => x.type === 'CsCodeGenDefaults');
            if (found?.body) return JSON.parse(found.body);

            return MOCK_DEFAULTS;
        } catch (error) {
            this.logError('getDefaults', error.message);
            return MOCK_DEFAULTS;
        }
    };

    /**
     * Gets every known CsCodeGenDefaults note (rk, title, parsed body) — used by
     * LoadCsCodeGenDefaultsDlg to let the user explicitly pick which Defaults profile to load,
     * instead of the ambiguous "first match" getDefaults()/getDefaultsRk() use for the header display.
     * @returns {Array<{rk:string, title:string, defaults:object}>}
     */
    getAllDefaultsEntries = () => {
        try {
            const items = csCodeTemplateCache
                .getAll()
                .filter((x) => x.type === 'CsCodeGenDefaults')
                // Backup copies (title contains "Backup") aren't meant to be loaded — exclude them
                // from the picker entirely, not just deprioritize them.
                .filter((x) => !x.title?.toLowerCase().includes('backup'));
            return items
                .map((x) => {
                    try {
                        return { rk: x.rk, title: x.title, remark: x.remark ?? '', defaults: JSON.parse(x.body) };
                    } catch {
                        return null;
                    }
                })
                .filter(Boolean);
        } catch (error) {
            this.logError('getAllDefaultsEntries', error.message);
            return [];
        }
    };

    /**
     * Gets the RK of the active CsCodeGenDefaults note, or null when falling back to MOCK_DEFAULTS
     * (BE cache empty). Used by the UI to show which note is the current source of truth for defaults.
     * @returns {string|null}
     */
    getDefaultsRk = () => {
        try {
            const items = csCodeTemplateCache.getAll();
            const found = items.find((x) => x.type === 'CsCodeGenDefaults');
            return found?.rk ?? null;
        } catch (error) {
            this.logError('getDefaultsRk', error.message);
            return null;
        }
    };

    /**
     * update template/defaults notes in cache
     * @param {*} entries the updated entries from BE from SignalR
     * @returns
     */
    onJournalUpdated = (entries) => {
        try {
            if (!Array.isArray(entries) || !entries.length) {
                return false;
            }

            const items = entries.filter((x) => x.type === 'CsCodeTemplate' || x.type === 'CsCodeGenDefaults');
            if (items.length === 0) {
                return;
            }

            csCodeTemplateCache.addOrUpdate(items);

            this._notify();
            return true;
        } catch (error) {
            this.logError('onJournalUpdated', error.message);
            return false;
        }
    };

    /**
     * Called directly from PageContentSRHandlers on OnJournalDeleted.
     * @param {string} rk
     */
    onJournalDeleted = (rk) => {
        try {
            if (!rk) {
                this.logError('onJournalDeleted', `Invalid row key - ${rk}`);
                return false;
            }

            const deleted = csCodeTemplateCache.delete(rk);
            if (!deleted) {
                return;
            }

            this._notify();

            return deleted;
        } catch (error) {
            this.logError('onJournalDeleted', error.message);
            return false;
        }
    };

    // ─── private ──────────────────────────────────────────

    _notify = () => {
        componentNotifier.notify(_name, 'dataChanged', {
            items: csCodeTemplateCache.getAll(),
        });
    };

    clear = () => {
        csCodeTemplateCache.clear();
        super.clear();
    };
}

const csCodeTemplateEngine = new CsCodeTemplateEngine();
export default csCodeTemplateEngine;
